/**
 * The frozen offer read the onboarding container mounts on. OQ-3: the offer
 * list is resolved once, on first settle, and never recomputed, so a fan who
 * adds all three methods does not watch the step delete itself and the whole
 * flow renumber underneath them. Only each row's `Added` state, read live by
 * `SignInMethodsStep` itself, changes after that.
 */
import { useIdentityMethodsGet } from "@sportsmet/auth-sdk/private";
import { useOauthProviderConfigGet } from "@sportsmet/auth-sdk/public";
import { useEffect, useState } from "react";

import {
  resolveProviderIds,
  resolveSignInMethodOffers,
  type typeSignInMethodOffer,
} from "./signInMethodOffers";

export type typeSignInMethodOffersState = {
  /** True until both reads have settled. The container shows its pending screen. */
  isPending: boolean;
  /** Frozen on first settle. Never recomputed. See plan OQ-3. */
  offers: readonly typeSignInMethodOffer[];
};

export function useSignInMethodOffers(): typeSignInMethodOffersState {
  const methodsQuery = useIdentityMethodsGet({});
  const configQuery = useOauthProviderConfigGet({});
  // An errored query is settled, not pending: its `data` is an error
  // envelope whose `data` is null, `resolveSignInMethodOffers` returns [],
  // and the fan simply does not get the step. A failed read must never trap
  // a fan in onboarding.
  const isSettling = methodsQuery.isPending || configQuery.isPending;

  const [frozen, setFrozen] = useState<readonly typeSignInMethodOffer[] | null>(
    null,
  );

  useEffect(() => {
    if (isSettling) return;
    if (frozen !== null) return;
    setFrozen(
      resolveSignInMethodOffers({
        methods: methodsQuery.data?.data ?? null,
        providerIds: resolveProviderIds(configQuery.data?.data),
      }),
    );
    // Deliberately not calling showApiError here: a fan who cannot be
    // offered a second sign-in method during onboarding has not hit an
    // error worth a modal, they get a five-step flow instead.
  }, [isSettling, frozen, methodsQuery.data, configQuery.data]);

  return { isPending: frozen === null, offers: frozen ?? [] };
}
